import { Formik } from "formik";
import * as React from "react";
import * as yup from "yup";
import styled from "styled-components";
import { toast } from "react-toastify";
import _ from "lodash";

import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import {
  FormRow,
  Input,
  Label,
  Select
} from "src/library/components/Forms";
import { Text } from "src/library/components/Text";
import { WysiwygEditor } from "src/library/components/WysiwygEditor";
import NotificationInfoSidebar from "src/core/notifications/components/NotificationInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  useNotificationsApi,
  INotificationRecipientsInfo,
  INotificationRecipientType,
  INotificationRecipient
} from "src/core/notifications/api/notifications.api";
import { PageProps } from "src/pages/Router/routes";
import { useCurrentUser } from "src/core/users/hooks";
import { CustomerMultiSelect } from "src/core/customers/components/CustomerMultiSelect";
import { DropMultiSelect } from "src/core/drops/components/DropMultiSelect";

interface NotificationFormValues {
  title: string;
  body: string;
  recipientType?: INotificationRecipientType;
  recipientMultipleCustomers?: string[];
  recipientSingleCustomer?: string;
  recipientDrops?: string[];
}
type INotificationRecipientsCreate = Partial<INotificationRecipient>;

// TODO: Global setting?
const notificationSchema = yup.object().shape({
  title: yup.string().required("Required"),
  body: yup.string().required("Required"),
  recipientType: yup
    .string()
    .required("Required"),
  recipientMultipleCustomers: yup.mixed().when("recipientType", {
    is: "customer",
    then: yup.array().required("Required"),
    otherwise: yup.array()
  }),
  recipientSingleCustomer: yup.mixed().when("recipientType", {
    is: "branch",
    then: yup
      .string()
      .required("Required")
  }),
  recipientDrops: yup.mixed().when("recipientType", {
    is: "branch",
    then: yup.array().required("Required"),
    otherwise: yup.array()
  })
});

const CreateNotificationPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology, history } = props;
  const [ createNotificationResponse, createNotification ] = useNotificationsApi("CREATE_NOTIFICATION");
  const [ notificationRecipientsInfoResponse, getNotificationRecipientsInfo ] = useNotificationsApi("GET_NOTIFICATION_RECIPIENTS_INFO");
  const [ { currentRole } ] = useCurrentUser();
  const [ currentCustomer, setCurrentCustomer ] = React.useState<number | null>(null);
  const [ notificationRecipients, setNotificationRecipients ] = React.useState<INotificationRecipientsCreate[]>([]);

  const [ notificationRecipientsInformation, setNotificationRecipientsInformation ] = React.useState<INotificationRecipientsInfo | undefined>(
    undefined
  );

  const [ fetchingNotificationInformation, setFetchingNotificationInformation ] = React.useState<boolean>(false);
  const [ isValidAudienceInformation, setIsValidAudienceInformation ] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (createNotificationResponse.error) {
      toast.error(createNotificationResponse.error.message);
    }

    // On Success reset the form.
    if (createNotificationResponse.data) {
      history.push(`/platform/notifications/${createNotificationResponse.data.id}/details`);
    }
  }, [ createNotificationResponse ]);

  // Get notification recipients information.
  React.useEffect(() => {
    const schema = yup.array(yup.object().shape({
      entityId: yup.mixed().when("type", {
        is: "global",
        then: yup
          .number()
          .nullable()
          .notRequired(),
        otherwise: yup
          .number()
          .required()
      }),
      type: yup.string().required()
    })).min(1);

    schema
      .validate(notificationRecipients)
      .then(value => {
        setFetchingNotificationInformation(true);

        getNotificationRecipientsInfo({
          entityIds: value.length ? JSON.stringify(value.map(n => n.entityId)) : undefined,
          type: value.length ? value[ 0 ].type : ""
        });
      })
      .catch(_error => {
        setIsValidAudienceInformation(false);
        setFetchingNotificationInformation(false);
      });
    setFetchingNotificationInformation(false);
  }, [ getNotificationRecipientsInfo, notificationRecipients ]);

  // Set notification information.
  React.useEffect(() => {
    if (notificationRecipientsInfoResponse && notificationRecipientsInfoResponse.data) {
      setNotificationRecipientsInformation(notificationRecipientsInfoResponse.data);
      setIsValidAudienceInformation(true);
    }

    setFetchingNotificationInformation(false);
  }, [ notificationRecipientsInfoResponse ]);

  const buildNotificationRecipients = (values: NotificationFormValues) => {
    setNotificationRecipientsInformation(undefined);
    const recipients: INotificationRecipient[] = [];

    if (values && values.recipientType === "customer" && values.recipientMultipleCustomers) {
      for (const customer of values.recipientMultipleCustomers) {
        recipients.push({
          entityId: parseInt(customer, 10),
          type: "customer"
        });
      }
    } else if (values && values.recipientType === "branch" && values.recipientDrops) {
      for (const drop of values.recipientDrops) {
        recipients.push({
          entityId: parseInt(drop, 10),
          type: "branch"
        });
      }
    } else if (values && values.recipientType === "global") {
      recipients.push({ type: "global" });
    }

    setNotificationRecipients(recipients);
  };

  const submitForm = async (values: NotificationFormValues) => {
    createNotification({
      title: values.title,
      body: values.body,
      notificationRecipients
    });
  };

  // Dont show all platform users option for an account manager
  // still allow them to send a notification to their customers though
  const sendToOptions = React.useMemo(() => {
    return _.compact([
      currentRole?.code !== "account-manager" ? {
        text: `All platform ${terminology.user.plural.text}`,
        value: "global"
      } : null,
      {
        text: `Multiple ${terminology.customer.plural.text}`,
        value: "customer"
      },
      {
        text: `Multiple ${terminology.customer.singular.text} ${terminology.drop.plural.text}`,
        value: "branch"
      }
    ]);
  }, [
    currentRole?.code,
    terminology.customer.plural.text,
    terminology.customer.singular.text,
    terminology.drop.plural.text,
    terminology.user.plural.text
  ]);

  return (
    <Formik
      initialValues={{
        title: "",
        body: "",
        recipientType: undefined,
        recipientMultipleCustomers: undefined,
        recipientSingleCustomer: undefined,
        recipientDrops: undefined
      }}
      validationSchema={notificationSchema}
      onSubmit={submitForm}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({
        values, handleSubmit, handleChange, setFieldValue, errors
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Layout {...props}>
              <MainContentWrapper fill>
                <FormSection
                  style={{
                    paddingLeft: "10px",
                    maxWidth: "1000px"
                  }}
                >
                  <Grid.Bounds>
                    <Grid.Box style={{ width: "100%" }}>
                      <FormRow>
                        <Grid.Box margin="0 0 10px 0">
                          <Input
                            value={values.title}
                            error={errors.title}
                            label={`${terminology.notification.singular.text} Title`}
                            name="title"
                            handleChange={handleChange}
                          />
                        </Grid.Box>

                        <WsywyigWrapper className={errors.body ? "errors" : ""}>
                          <Label
                            for="body"
                            label="Notification Body"
                          />

                          <WysiwygEditor
                            frame
                            handleChange={value => setFieldValue("body", value)}
                          />

                          {errors.body && (
                            <Text>
                              {errors.body}
                            </Text>
                          )}
                        </WsywyigWrapper>
                      </FormRow>
                    </Grid.Box>
                  </Grid.Bounds>

                  <Grid.Bounds
                    margin="30px 0"
                    direction="vertical"
                  >
                    <FormRow>
                      <Grid.Bounds
                        margin="0 0 10px 0"
                        direction="vertical"
                        className="recipientType-wrapper"
                      >
                        <Select
                          value={values.recipientType}
                          name="recipientType"
                          handleChange={handleChange}
                          label="Send to"
                          unselectedOption={{
                            text: "Please select",
                            value: ""
                          }}
                          options={sendToOptions}
                          error={errors.recipientType}
                          fieldLogic={fieldValue => {
                            buildNotificationRecipients({
                              ...values,
                              recipientDrops: undefined,
                              recipientMultipleCustomers: undefined,
                              recipientSingleCustomer: undefined,
                              recipientType: fieldValue
                            });
                            setFieldValue("recipientType", fieldValue);
                            setFieldValue("recipientDrops", undefined);
                            setFieldValue("recipientMultipleCustomers", undefined);
                          }}
                        />
                      </Grid.Bounds>

                      {/* Select Multiple Customers. */}
                      {values.recipientType === "customer" && (
                        <Grid.Bounds
                          direction="vertical"
                          className="recipientType-wrapper"
                        >
                          <CustomerMultiSelect
                            onCustomersChange={({ fullCustomersSelected }) => {
                              buildNotificationRecipients({
                                ...values,
                                recipientMultipleCustomers: fullCustomersSelected.map(c => c.id.toString())
                              });
                              setFieldValue("recipientMultipleCustomers", fullCustomersSelected.map(c => c.id));
                            }}
                          />

                        </Grid.Bounds>
                      )}

                      {/* Select One customer/multiple drips. */}
                      {values.recipientType === "branch" && (
                        <Grid.Box>
                          <Grid.Bounds
                            margin="0 0 10px 0"
                            direction="vertical"
                            className="recipientSingleCustomer-wrapper"
                          >

                            <CustomerMultiSelect
                              onCustomersChange={({ fullCustomersSelected }) => {
                                buildNotificationRecipients({
                                  ...values,
                                  recipientSingleCustomer: fullCustomersSelected[ 0 ]?.id.toString()
                                });
                                setFieldValue("recipientSingleCustomer", fullCustomersSelected[ 0 ]?.id);
                                setCurrentCustomer(fullCustomersSelected[ 0 ]?.id);
                              }}
                              singleSelection
                            />
                          </Grid.Bounds>

                          <Grid.Bounds
                            direction="vertical"
                            className="recipientDrops-wrapper"
                          >
                            {currentCustomer && (
                              <DropMultiSelect
                                onDropsChange={({ fullDropsSelected }) => {
                                  buildNotificationRecipients({
                                    ...values,
                                    recipientDrops: fullDropsSelected.map(d => d.id.toString())
                                  });
                                  setFieldValue("recipientDrops", fullDropsSelected);
                                }}
                                dropFilters={{ customerId: currentCustomer }}
                              />
                            )}
                          </Grid.Bounds>
                        </Grid.Box>
                      )}
                    </FormRow>
                  </Grid.Bounds>
                </FormSection>
              </MainContentWrapper>

              <InfoBarWrapper>
                <NotificationInfoSidebar
                  title="Audience information"
                  isValidAudienceInformation={isValidAudienceInformation}
                  terminology={terminology}
                  isLoading={fetchingNotificationInformation}
                  audienceInformation={notificationRecipientsInformation}
                  primaryAction={(
                    <Button
                      $buttonType="primary"
                      background="lightGrey"
                      onClick={() => history.goBack()}
                    >
                      Cancel
                    </Button>
                  )}
                  secondaryAction={(
                    <Button
                      disabled={createNotificationResponse.pending}
                      loading={notificationRecipientsInfoResponse.pending}
                      $buttonType="submit"
                      type="submit"
                      background="primaryDark"
                    >
                      Send Notification
                    </Button>
                  )}
                />
              </InfoBarWrapper>
            </Layout>
          </form>
        );
      }}
    </Formik>
  );
};

const WsywyigWrapper = styled(Grid.Box)`
  margin: 0 0 10px 0;

  &.errors {
    .frame {
      border-color: ${({ theme }) => theme.colors.error};
    }
    span {
      color: ${({ theme }) => theme.colors.error};
    }
  }
`;

const FormSection = styled.div`
  margin: 20px 0;
  overflow: scroll;
  height: 1000px;

  .price-wrapper {
    flex-wrap: wrap;
  }
  .cutoffDays-wrapper {
    flex-wrap: wrap;

    div:first-child {
      width: 100%;
    }
  }

  ${({ theme }) => `
    border-bottom: 1px solid ${theme.colors.lightGrey}

    h2 {
      font-size: ${theme.fonts.sizes.h2};
    }
  `};
`;

export default CreateNotificationPage;
